import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down_filled.svg";
import UsersIcon from "@redotech/redo-icons/users-01.svg";
import XIcon from "@redotech/redo-icons/x_filled.svg";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { LabelText } from "@redotech/redo-web/arbiter-components/common/descriptors";
import {
  RedoFilterDropdownAnchor,
  RedoFilterGroup,
  RedoFilterSection,
} from "@redotech/redo-web/arbiter-components/filter-group/redo-filter-group";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { RedoAdminRpcClientContext } from "../../app/redo-admin-rpc-client-provider";

interface TeamOption {
  _id: string;
  name: string;
}

// Only resolve names for the first few selected teams; the rest collapse into a
// "+N more" so a price book with thousands of teams doesn't fetch every name.
const MAX_DISPLAYED_TEAMS = 5;

export interface TeamMultiSelectProps {
  selectedTeamIds: string[];
  onTeamsChange: (teamIds: string[]) => void;
  label?: string;
  placeholder?: string;
  emptyText?: string;
  connectiveText?: string;
  variant?: "input" | "filter";
  openOnRender?: boolean;
  removeFilter?: () => void;
}

export const TeamMultiSelect = memo(function TeamMultiSelect({
  selectedTeamIds,
  onTeamsChange,
  label = "Filter by Teams",
  placeholder = "Search teams by name...",
  emptyText = "All teams (no filter applied)",
  connectiveText = "include",
  variant = "input",
  openOnRender = false,
  removeFilter,
}: TeamMultiSelectProps) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [searchQuery, setSearchQuery] = useState("");
  const [teamOptions, setTeamOptions] = useState<TeamOption[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<TeamOption[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const loadedTeamIdsRef = useRef<Set<string>>(new Set());
  const selectedTeamIdsRef = useRef(selectedTeamIds);
  selectedTeamIdsRef.current = selectedTeamIds;

  useEffect(() => {
    if (openOnRender && dropdownAnchor) {
      dropdownAnchor.click();
    }
  }, [dropdownAnchor, openOnRender]);

  useEffect(() => {
    const loadTeamNames = async () => {
      if (selectedTeamIds.length === 0) {
        setSelectedTeams([]);
        loadedTeamIdsRef.current.clear();
        return;
      }

      setSelectedTeams((currentTeams) =>
        currentTeams.filter((t) => selectedTeamIds.includes(t._id)),
      );

      const newIds = selectedTeamIds
        .slice(0, MAX_DISPLAYED_TEAMS)
        .filter((id) => !loadedTeamIdsRef.current.has(id));

      if (newIds.length === 0) {
        return;
      }

      for (const teamId of newIds) {
        try {
          const result = await rpcClient.searchTeams({
            query: teamId,
            limit: 1,
          });
          const team = result.teams[0];
          if (team) {
            setSelectedTeams((prev) =>
              prev.some((selectedTeam) => selectedTeam._id === team._id)
                ? prev
                : [...prev, team],
            );
            loadedTeamIdsRef.current.add(teamId);
          }
        } catch (error) {
          console.error(`Failed to load team ${teamId}:`, error);
        }
      }
    };

    void loadTeamNames();
  }, [selectedTeamIds, rpcClient]);

  const searchTeams = useHandler(async (query: string) => {
    const trimmedQuery = query.trim();
    const minimumSearchLength = variant === "filter" ? 0 : 2;

    if (trimmedQuery.length < minimumSearchLength) {
      setTeamOptions([]);
      return;
    }

    setIsSearching(true);
    try {
      const result = await rpcClient.searchTeams({
        query: trimmedQuery,
        limit: variant === "filter" ? 100 : 10,
      });
      setTeamOptions(result.teams);
    } catch (error) {
      console.error("Team search failed:", error);
      setTeamOptions([]);
    } finally {
      setIsSearching(false);
    }
  });

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (variant === "filter" && !dropdownOpen) {
      return;
    }

    const minimumSearchLength = variant === "filter" ? 0 : 2;

    if (searchQuery.trim().length >= minimumSearchLength) {
      searchTimeoutRef.current = setTimeout(async () => {
        await searchTeams(searchQuery);
      }, 300);
    } else {
      setTeamOptions([]);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [dropdownOpen, searchQuery, searchTeams, variant]);

  const handleTeamRemove = useHandler((teamId: string) => {
    onTeamsChange(selectedTeamIds.filter((id) => id !== teamId));
  });

  const handleDropdownOpenChange = useHandler((open: boolean) => {
    setDropdownOpen(open);
    if (!open) {
      setSearchQuery("");
      if (
        variant === "filter" &&
        selectedTeamIdsRef.current.length === 0 &&
        removeFilter
      ) {
        removeFilter();
      }
    }
  });

  const handleButtonClick = useHandler(() => {
    handleDropdownOpenChange(!dropdownOpen);
  });

  const displayedTeamOptions = useMemo(() => {
    const optionsById = new Map<string, TeamOption>();
    for (const team of selectedTeams) {
      optionsById.set(team._id, team);
    }
    for (const team of teamOptions) {
      optionsById.set(team._id, team);
    }
    return [...optionsById.values()];
  }, [selectedTeams, teamOptions]);

  const listItems: RedoListItem<TeamOption>[] = useMemo(
    () =>
      displayedTeamOptions.map((team) => ({
        type: "text",
        id: team._id,
        value: team,
        text: team.name,
        supportingText: team._id,
      })),
    [displayedTeamOptions],
  );

  const nameById = useMemo(
    () => new Map(selectedTeams.map((team) => [team._id, team.name])),
    [selectedTeams],
  );

  // Every selected id is a selected item (name where loaded, id as fallback) so
  // editing never drops teams whose names we didn't fetch.
  const selectedListItems: RedoListItem<TeamOption>[] = useMemo(
    () =>
      selectedTeamIds.map((id) => {
        const name = nameById.get(id) ?? id;
        return {
          type: "text",
          id,
          value: { _id: id, name },
          text: name,
          supportingText: id,
        };
      }),
    [selectedTeamIds, nameById],
  );

  const selectedTeamsText = useMemo(() => {
    if (selectedTeamIds.length === 0) {
      return "...";
    }

    if (selectedTeamIds.length === 1) {
      return selectedTeams[0]?.name ?? selectedTeamIds[0] ?? "1 Team";
    }

    return `${selectedTeamIds.length} Teams`;
  }, [selectedTeamIds, selectedTeams]);

  const extraTeamCount = Math.max(
    0,
    selectedTeamIds.length - MAX_DISPLAYED_TEAMS,
  );
  const selectedTeamsTooltip = selectedTeamIds.length > 0 && (
    <Flex dir="column">
      {selectedTeamIds.slice(0, MAX_DISPLAYED_TEAMS).map((id) => (
        <Text key={id}>{nameById.get(id) ?? id}</Text>
      ))}
      {extraTeamCount > 0 && (
        <Text>
          and {extraTeamCount} more {extraTeamCount === 1 ? "team" : "teams"}
        </Text>
      )}
    </Flex>
  );

  const handleSelectionChange = useHandler(
    (newSelectedItems: RedoListItem<TeamOption>[]) => {
      const newTeamIds = newSelectedItems.map((item) => item.value._id);
      selectedTeamIdsRef.current = newTeamIds;
      // Keep only display names we actually know (loaded, or picked from search),
      // pruned to the current selection; unknown ids stay in the "+N more".
      setSelectedTeams((prev) => {
        const byId = new Map<string, TeamOption>(prev.map((t) => [t._id, t]));
        for (const item of newSelectedItems) {
          const team = item.value;
          if (team.name && team.name !== team._id) {
            byId.set(team._id, team);
            loadedTeamIdsRef.current.add(team._id);
          }
        }
        return newTeamIds
          .map((id) => byId.get(id))
          .filter((t): t is TeamOption => t != null);
      });
      onTeamsChange(newTeamIds);
      setSearchQuery("");
    },
  );

  const teamDropdown = (
    <RedoMultiselectDropdown
      dropdownAnchor={dropdownAnchor}
      dropdownOpen={dropdownOpen}
      emptyListMessage={
        isSearching
          ? "Searching teams..."
          : searchQuery.trim().length >= (variant === "filter" ? 0 : 2)
            ? "No teams found"
            : "Start typing to search"
      }
      fitToAnchor={variant === "input"}
      items={listItems}
      searchPlaceholder={placeholder}
      searchString={searchQuery}
      selectedItems={selectedListItems}
      selectionVariant="checkbox"
      setDropdownOpen={handleDropdownOpenChange}
      setSearchString={setSearchQuery}
      setSelectedItems={handleSelectionChange}
      size={variant === "filter" ? "xs" : "sm"}
    />
  );

  if (variant === "filter") {
    if (selectedTeamIds.length === 0 && !openOnRender) {
      return null;
    }

    return (
      <>
        {teamDropdown}
        <RedoFilterGroup
          Icon={UsersIcon}
          propertyName={label}
          query={<RedoFilterSection color="secondary" text={connectiveText} />}
          removeFilter={removeFilter}
          value={
            <RedoFilterDropdownAnchor
              color="primary"
              onClick={handleButtonClick}
              ref={setDropdownAnchor}
              text={selectedTeamsText}
              tooltip={selectedTeamsTooltip}
              weight="medium"
            />
          }
        />
      </>
    );
  }

  return (
    <Flex dir="column" gap="sm" w="full">
      <Flex dir="column" gap="sm">
        <LabelText label={label} size="sm" />
        <RedoButton
          hierarchy="secondary"
          IconTrailing={ChevronDownIcon}
          onClick={handleButtonClick}
          ref={setDropdownAnchor}
          size="md"
          text={
            selectedTeamIds.length > 0
              ? `${selectedTeamIds.length} team${selectedTeamIds.length > 1 ? "s" : ""} selected`
              : placeholder
          }
        />
      </Flex>

      <RedoMultiselectDropdown
        dropdownAnchor={dropdownAnchor}
        dropdownOpen={dropdownOpen}
        emptyListMessage={
          isSearching
            ? "Searching teams..."
            : searchQuery.trim().length >= 2
              ? "No teams found"
              : "Start typing to search"
        }
        fitToAnchor
        items={listItems}
        searchPlaceholder={placeholder}
        searchString={searchQuery}
        selectedItems={selectedListItems}
        selectionVariant="checkbox"
        setDropdownOpen={handleDropdownOpenChange}
        setSearchString={setSearchQuery}
        setSelectedItems={handleSelectionChange}
        size="sm"
      />

      {selectedTeamIds.length > 0 && (
        <Flex gap="xs" wrap="wrap">
          {selectedTeamIds.slice(0, MAX_DISPLAYED_TEAMS).map((id) => (
            <RedoBadge
              color="blue"
              iconTrailing={{
                type: "icon",
                Icon: XIcon,
                onClick: () => handleTeamRemove(id),
              }}
              key={id}
              size="md"
              text={nameById.get(id) ?? id}
            />
          ))}
          {extraTeamCount > 0 && (
            <RedoBadge
              color="gray"
              size="md"
              text={`+${extraTeamCount} more`}
            />
          )}
        </Flex>
      )}

      {selectedTeamIds.length === 0 && (
        <Flex align="center" justify="center" p="sm">
          <Text color="secondary" fontSize="xs">
            {emptyText}
          </Text>
        </Flex>
      )}
    </Flex>
  );
});
